import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock, Database, Mail, Users, Trash2, Cookie, Globe2, ScrollText, Baby } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

/**
 * Public Privacy Policy page.
 *
 * WHY THIS EXISTS:
 * - Google Play Store REQUIRES a publicly reachable privacy policy URL for
 *   every app that handles personal data. This page satisfies that requirement.
 * - Apple App Store and PWA install prompts also surface this link.
 * - The page is intentionally unauthenticated (no /app/* wrapper) so crawlers,
 *   store reviewers, and prospective users can read it without signing in.
 *
 * KEEP IN SYNC WITH: /terms.  If you materially change data practices
 * (new subprocessor, new integration, new retention window), bump the
 * "Last updated" date at the bottom so users see the change on next visit.
 */

const SECTIONS = [
  {
    id: "summary",
    icon: ShieldCheck,
    title: "The short version",
    body: (
      <ul className="space-y-2">
        <li>Impo is a private, AI-powered memory vault. Your data is yours — we don't sell it, we don't train external models on it, and we don't share it with advertisers.</li>
        <li>Passwords stored in your vault are encrypted server-side (Fernet). Only your account can decrypt them.</li>
        <li>You can export or delete everything you've stored at any time from Profile → Data controls.</li>
        <li>We use Claude (Anthropic) for AI features. Prompts are sent transiently, not retained by us for training, and never labelled with your identity.</li>
      </ul>
    ),
  },
  {
    id: "data-we-collect",
    icon: Database,
    title: "Data we collect",
    body: (
      <>
        <p className="mb-3">We only collect what's needed to run the product for you:</p>
        <ul className="space-y-2 list-disc pl-5">
          <li><strong>Account:</strong> email or phone number, hashed password, subscription plan, timezone.</li>
          <li><strong>Content you save:</strong> notes, documents, photos, videos, passwords, journal entries, reminders, and any other item you choose to add. This is the app's core purpose.</li>
          <li><strong>Device metadata:</strong> installed passkey credentials (public key only), push-notification subscription tokens if you enable them.</li>
          <li><strong>Diagnostics:</strong> minimal server logs (IP, user-agent, error stack traces) for debugging and abuse prevention. Kept 30 days, then rotated.</li>
          <li><strong>Product analytics:</strong> we use PostHog to understand feature usage in aggregate. Events are keyed to a pseudonymous ID; we don't send raw content to PostHog.</li>
        </ul>
      </>
    ),
  },
  {
    id: "data-we-dont",
    icon: Lock,
    title: "What we DON'T do",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li>We don't sell your data. Ever.</li>
        <li>We don't use your content to train AI models.</li>
        <li>We don't share your content with advertisers or data brokers.</li>
        <li>We don't read your vault. Passwords remain encrypted at rest — support staff cannot see them.</li>
        <li>We don't attach identifying info to AI prompts sent to third parties.</li>
      </ul>
    ),
  },
  {
    id: "subprocessors",
    icon: Users,
    title: "Who processes your data on our behalf",
    body: (
      <>
        <p className="mb-3">We use a small number of trusted subprocessors:</p>
        <ul className="space-y-2 list-disc pl-5">
          <li><strong>Anthropic (Claude):</strong> AI intent routing and vision categorization. Prompts sent transiently; not retained for training.</li>
          <li><strong>OpenAI (Whisper) — optional:</strong> only if you connect your own OpenAI API key for voice transcription. Audio never touches OpenAI unless you opt in.</li>
          <li><strong>Google (OAuth &amp; Calendar sync) — optional:</strong> only if you connect a Google account. Impo requests the minimum scopes required.</li>
          <li><strong>Razorpay:</strong> payment processing for subscriptions. Card data never touches Impo servers.</li>
          <li><strong>MongoDB Atlas:</strong> managed database hosting.</li>
          <li><strong>PostHog:</strong> aggregate product analytics (see “Data we collect” above).</li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use-it",
    icon: ScrollText,
    title: "How we use your data",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li><strong>To operate the product:</strong> storing notes, documents, running search, delivering reminders, syncing Calendar, etc.</li>
        <li><strong>To power AI features:</strong> your prompts and — for images marked for classification — the image are sent to Anthropic transiently. No account identifier accompanies these requests.</li>
        <li><strong>To keep the service safe:</strong> throttling abuse, detecting fraud, and honoring lawful requests.</li>
        <li><strong>To improve the product:</strong> aggregate, non-identifying usage patterns (which features are used, error rates, etc.) via PostHog.</li>
      </ul>
    ),
  },
  {
    id: "retention",
    icon: Trash2,
    title: "How long we keep data",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li><strong>Your content:</strong> retained as long as your account is active. When you delete an item it is soft-deleted immediately and hard-deleted from primary storage within 30 days. Backup snapshots roll over within 60 days.</li>
        <li><strong>Account deletion:</strong> when you delete your account (Profile → Data controls → Delete account) all associated content, files, and backups are removed within 60 days. Some records (payment invoices, security logs) may be retained longer where legally required.</li>
        <li><strong>Server logs:</strong> rotated after 30 days.</li>
      </ul>
    ),
  },
  {
    id: "your-rights",
    icon: ShieldCheck,
    title: "Your rights",
    body: (
      <>
        <p className="mb-3">Regardless of where you live, you can:</p>
        <ul className="space-y-2 list-disc pl-5">
          <li><strong>Access &amp; export:</strong> download all your data as JSON from Profile → Backup &amp; restore.</li>
          <li><strong>Correct:</strong> edit any item directly, or contact support for account fields.</li>
          <li><strong>Delete:</strong> individual items via the delete-with-undo flow, or the entire account via Profile → Data controls.</li>
          <li><strong>Object &amp; restrict:</strong> disable optional integrations (Voice transcription, Google Calendar sync) at any time.</li>
          <li><strong>Portability:</strong> the JSON export is a portable, human-readable format.</li>
          <li><strong>Complain:</strong> if you're in the EU/UK you may lodge a complaint with your local data protection authority; in India you may contact the Data Protection Board.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    icon: Cookie,
    title: "Cookies &amp; local storage",
    body: (
      <ul className="space-y-2 list-disc pl-5">
        <li><strong>Authentication:</strong> we store a JWT token in localStorage to keep you signed in. Clearing your browser data logs you out.</li>
        <li><strong>Offline queue:</strong> we use IndexedDB to hold writes made while offline; they sync when you're back online, then are removed.</li>
        <li><strong>Preferences:</strong> your theme choice (light/dark) is stored locally.</li>
        <li><strong>Analytics:</strong> PostHog sets a pseudonymous ID cookie for aggregate metrics. It's not used for advertising.</li>
      </ul>
    ),
  },
  {
    id: "international",
    icon: Globe2,
    title: "International transfers",
    body: (
      <p>
        Our primary infrastructure is hosted in the region best suited for latency and legal compliance for our user base. AI features may transiently transfer prompt data to Anthropic infrastructure in the United States. When these transfers happen, we rely on Standard Contractual Clauses and/or vendor-specific data-processing agreements for lawful cross-border transfer.
      </p>
    ),
  },
  {
    id: "children",
    icon: Baby,
    title: "Children's privacy",
    body: (
      <p>
        Impo is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided us data, please contact us at{" "}
        <a href="mailto:hello@impoapp.com" className="text-primary underline">hello@impoapp.com</a>{" "}and we will delete it.
      </p>
    ),
  },
  {
    id: "contact",
    icon: Mail,
    title: "Contact us",
    body: (
      <>
        <p>
          Privacy questions, deletion requests, or feedback:{" "}
          <a href="mailto:hello@impoapp.com" className="text-primary underline">hello@impoapp.com</a>.
        </p>
        <p className="mt-3 text-xs">
          If you're a data-protection authority reaching out on behalf of a user, please include the affected account identifier in your request so we can respond within statutory deadlines.
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  const nav = useNavigate();
  useEffect(() => { document.title = "Impo — Privacy Policy"; }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      {/* Public header — no auth required. */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} data-testid="privacy-back-button" aria-label="Go back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-logo-link">
            <Logo className="h-6" />
          </Link>
          <div className="ml-auto text-[11px] text-muted-foreground">Legal</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Privacy Policy</div>
          <h1 className="font-display text-3xl font-bold leading-tight" data-testid="privacy-title">How we handle your data</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Impo is a personal memory OS. Trust is the entire product. This document explains, in plain language, what we collect, why, and the choices you always have.
          </p>
        </div>

        {/* TOC */}
        <nav aria-label="Table of contents" className="rounded-2xl border border-border bg-card p-4" data-testid="privacy-toc">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">On this page</div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-3 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-primary hover:underline">
                  {s.title.replace(/&amp;/g, "&")}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-5">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className="rounded-2xl border border-border bg-card p-5"
              data-testid={`privacy-section-${s.id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                  <s.icon className="h-5 w-5" />
                </div>
                <h2 className="font-display font-semibold text-lg">
                  {s.title.replace(/&amp;/g, "&")}
                </h2>
              </div>
              <div className="text-sm text-foreground/85 leading-relaxed">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <footer className="pt-4 pb-8 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>Last updated: 22 July 2026</div>
          <div className="flex items-center gap-3">
            <Link to="/terms" className="underline" data-testid="privacy-footer-terms">Terms</Link>
            <Link to="/" className="underline" data-testid="privacy-footer-home">Home</Link>
            <a href="mailto:hello@impoapp.com" className="underline">Contact</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
